import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["style", "prefixCls", "className", "theme", "editProps", "codePenOption", "codeSandboxOption", "code", "dependencies", "btnText", "btnHideText", "onlyEdit", "bordered", "noCode", "noPreview", "noScroll", "bgWhite", "sourceState"];
import React, { useRef, useEffect, useImperativeHandle } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import copyTextToClipboard from '@uiw/copy-to-clipboard';
import { javascript } from '@codemirror/lang-javascript';
import Split from '@uiw/react-split';
import ThirdPartyButton from "./ThirdPartyButton.js";
import * as icon from "./icon.js";
import { ErrorMessage } from "./ErrorMessage.js";
import { useCodePreview } from "./useCodePreview.js";
import "./index.css";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var CodePreview = /*#__PURE__*/React.forwardRef((props, ref) => {
  var {
      style,
      prefixCls = 'w-code-preview',
      className,
      theme = 'light',
      editProps = {},
      codePenOption,
      codeSandboxOption,
      code: _Code = '',
      dependencies = {},
      btnText = 'Code',
      btnHideText = 'Hide Editor',
      onlyEdit = false,
      bordered = true,
      noCode = false,
      noPreview = false,
      noScroll = false,
      bgWhite = false,
      sourceState
    } = props,
    otherProps = _objectWithoutPropertiesLoose(props, _excluded);
  var {
    playerId,
    setDemoDom,
    code,
    setCode,
    fullScreen,
    setFullScreen,
    errorMessage,
    width,
    setWidth,
    showEdit,
    setShowEdit,
    copied,
    setCopied
  } = useCodePreview(_extends({
    code: _Code,
    dependencies
  }, props));
  var demoRef = useRef(null);
  var editor = useRef(null);
  useImperativeHandle(ref, () => ({
    editor: editor,
    demo: demoRef.current
  }), [editor, demoRef]);
  var isOneItem = !noCode && !noPreview ? false : !noCode || !noPreview;
  var visiable = width === 1 ? false : [isOneItem ? 1 : 2];
  var cls = [className, prefixCls, noScroll ? prefixCls + "-noScroll" : null, isOneItem ? prefixCls + "-OneItem" : null, bordered ? prefixCls + "-bordered" : null, fullScreen ? prefixCls + "-fullScreen" : null].filter(Boolean).join(' ').trim();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setCode(_Code), [_Code]);
  useEffect(() => {
    if (demoRef.current) {
      setDemoDom(demoRef.current);
    }
    window.addEventListener('popstate', e => {
      document.body.style.overflow = 'inherit';
    }, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var transitionend = () => setShowEdit(width !== 1);
  useEffect(() => {
    var dom = demoRef.current;
    if (dom) {
      dom.addEventListener('transitionend', transitionend);
    }
    return () => {
      if (dom) {
        dom.removeEventListener('transitionend', transitionend);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);
  var handleChange = (value, viewUpdate) => {
    setCode(value);
    if (editProps && editProps.onChange) {
      editProps.onChange(value, viewUpdate);
    }
  };
  var onSwitchSource = () => {
    setWidth(width === 1 ? '50%' : 1);
    setShowEdit(true);
  };
  // 通过状态props属性判断是否切换源码
  var isShown = sourceState === 'shown';
  useEffect(() => {
    setWidth(isShown ? '50%' : 1);
    setShowEdit(isShown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShown]);
  var onCopyCode = () => {
    copyTextToClipboard(code || '', isCopy => setCopied(isCopy));
    setTimeout(() => setCopied(false), 2000);
  };
  var onFullScreen = () => {
    setFullScreen(!fullScreen);
    document.body.style.overflow = !fullScreen ? 'hidden' : 'inherit';
    if (!fullScreen && demoRef.current) {
      demoRef.current.style.maxWidth = 'inherit';
    }
  };
  return /*#__PURE__*/_jsxs(Split, _extends({
    "data-color-mode": theme,
    visiable: visiable,
    className: cls,
    style: _extends({
      flex: 1
    }, style)
  }, otherProps, {
    children: [!onlyEdit && /*#__PURE__*/_jsxs("div", {
      className: [prefixCls + "-demo", !bgWhite ? prefixCls + "-demo-bgPlaid" : null, errorMessage ? prefixCls + "-demo-error" : null].filter(Boolean).join(' ').trim(),
      style: _extends({
        flex: 1,
        display: !noPreview ? 'unset' : 'none'
      }, width === 1 ? {
        width: '100%'
      } : {}),
      children: [/*#__PURE__*/_jsx(ErrorMessage, {
        message: errorMessage
      }), /*#__PURE__*/_jsx("div", {
        className: [prefixCls + "-demo-source", errorMessage ? 'error' : null].filter(Boolean).join(' ').trim(),
        id: playerId.current
      })]
    }), (!noCode || onlyEdit) && /*#__PURE__*/_jsx("div", {
      ref: demoRef,
      style: {
        overflow: 'hidden',
        width: onlyEdit ? '100%' : width
      },
      children: (showEdit || onlyEdit) && /*#__PURE__*/_jsx(CodeMirror, _extends({
        value: (code || '').replace(/\n$/, ''),
        ref: editor,
        theme: theme,
        extensions: [javascript({
          jsx: true
        })]
      }, editProps, {
        style: {
          height: '100%'
        },
        height: "100%",
        onChange: handleChange
      }))
    }), !isOneItem && !(noCode && noPreview) && !onlyEdit && /*#__PURE__*/_jsxs("div", {
      style: {
        flex: 1,
        width: 29
      },
      className: prefixCls + "-bar",
      children: [/*#__PURE__*/_jsx(ThirdPartyButton, {
        prefixCls: prefixCls,
        codePenOption: codePenOption,
        codeSandboxOption: codeSandboxOption
      }), /*#__PURE__*/_jsx("div", {
        className: prefixCls + "-bar-btn",
        onClick: onSwitchSource,
        children: width === 1 ? btnText : btnHideText
      }), /*#__PURE__*/_jsx("div", {
        className: [prefixCls + "-bar-iconbtns", copied ? prefixCls + "-bar-copied" : null].filter(Boolean).join(' ').trim(),
        onClick: onCopyCode,
        children: copied ? icon.copyOk : icon.copy
      }), /*#__PURE__*/_jsx("div", {
        className: [prefixCls + "-bar-iconbtns", fullScreen ? prefixCls + "-bar-copied" : null].filter(Boolean).join(' ').trim(),
        onClick: onFullScreen,
        children: icon.full
      })]
    })]
  }));
});
CodePreview.displayName = 'CodePreview';
export default CodePreview;